import { useCallback, useEffect, useId, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { date as dfDateOnly } from '@orgazone/date-format';
import { api, MSG_TYPES, type ApiMailSettings, type ApiRetention, type ApiSchedule, type ApiSchedulesView, type ApiSupplierRow } from '../api';
import { Btn, Card, DataGate, Dot, PageHead, type DotKind } from '../components/ui';
import { useApp } from '../state';
import { useResource } from '../useResource';
import { schedCalc, schedDefaults, type SchedVals } from '../sched';
import { RhythmAssistant } from '../components/rhythm-assistant';
import { Picker, type PickerOption } from '@orgazone/ui-web';
import { fetchStateOf, WHY_TEXT } from '../fetch-state';

// Paramètres (§9/§10/§11) — 3 onglets CÂBLÉS sur l'API : Général (alertes, dead-letter, fuseau, dispatch →
// table clé/valeur `edi_setting`), Planifications (assistant de rythme → planif GLOBALE `schedule`), Rétention
// (durée par type → `retention_policy`). Tout est réel (§23.0) : plus aucun « démo », les valeurs viennent/
// repartent en base.

const lab = { fontSize: 'var(--t-sub)', fontWeight: 600, letterSpacing: '.04em', textTransform: 'uppercase' as const, color: 'var(--ink-500)', display: 'block', marginBottom: 5 };
const inputSt = { width: '100%', height: 38, padding: '0 12px', borderRadius: 'var(--r-sm)', border: '1px solid var(--ink-200)', background: 'var(--paper)', color: 'var(--ink-900)', fontSize: 'var(--t-lead)' } as const;
const str = (v: unknown, d = '') => (typeof v === 'string' ? v : typeof v === 'number' ? String(v) : d);

// Parse la config de rythme sérialisée dans `schedule.expression` (JSON SchedVals) ; défauts si absent/illisible.
function parseSched(expr: string | undefined): SchedVals {
  if (!expr) return schedDefaults();
  try { return { ...schedDefaults(), ...(JSON.parse(expr) as Partial<SchedVals>) }; } catch { return schedDefaults(); }
}

export function Parametres() {
  /**
   * ⚠️ L'ONGLET S'OUVRE PAR L'URL (`?onglet=sched`) — relevé de la maquette, dont le bandeau « aucun
   * échange » renvoie vers les PLANIFICATIONS et non vers l'écran. Un renvoi qui atterrit sur le premier
   * onglet laisse l'opérateur chercher ce qu'on vient de lui désigner ; c'est la même famille que la
   * consigne qui pointait un onglet vide des Référentiels.
   * ⚠️ Un onglet inconnu dans l'URL retombe sur « Général » plutôt que d'afficher un écran vide.
   */
  const [sp] = useSearchParams();
  const demande = sp.get('onglet');
  const [tab, setTab] = useState<'general' | 'sched' | 'retention' | 'mail'>(
    demande === 'sched' || demande === 'retention' || demande === 'mail' ? demande : 'general');

  // Les trois réglages partent ENSEMBLE : les onglets forment un seul écran, un chargement partiel
  // n'aurait rien à montrer. ⚠️ Aucun `isEmpty` : l'écran garde sa structure (onglets) même sans réglage.
  const { state, data, reload, error } = useResource(async (signal) => {
    // ⚠️ LES RYTHMES EFFECTIFS ET LES FOURNISSEURS PARTENT AVEC : l'onglet Planifications ne peut pas dire
    //    la vérité avec l'un sans l'autre — la liste des rythmes ne contient QUE les fournisseurs
    //    relevables, donc sans la liste complète on ne saurait pas nommer ceux qui ne le sont pas.
    const [settings, schedules, retention, view, suppliers] = await Promise.all([
      api.get<Record<string, unknown>>('/admin/settings', signal),
      api.get<ApiSchedule[]>('/admin/schedules', signal),
      api.get<ApiRetention[]>('/admin/retention', signal),
      api.get<ApiSchedulesView>('/admin/schedules/effectifs', signal),
      api.get<ApiSupplierRow[]>('/admin/suppliers', signal),
    ]);
    return { settings, schedules, retention, view, suppliers };
  });

  const settings = data?.settings ?? {};
  const retention = data?.retention ?? [];

  const tabs: [typeof tab, string][] = [['general', 'Général'], ['sched', 'Planifications'], ['retention', 'Rétention'], ['mail', 'Messagerie']];
  const pageHead = (
    <PageHead title="Paramètres" sub="Réglages de la plateforme EDI — alertes, rythme de relève, durées de conservation, messagerie" />
  );

  return (
    <DataGate state={state} onRetry={reload} error={error} head={pageHead}>
      <div className="seg-track" style={{ marginBottom: 18 }}>
        {tabs.map(([id, label]) => (
          <button aria-pressed={tab === id} key={id} type="button" onClick={() => setTab(id)} className={tab === id ? 'seg active' : 'seg'}>{label}</button>
        ))}
      </div>
      {tab === 'general' && <General settings={settings} onSaved={reload} />}
      {tab === 'sched' && <Planifications onSaved={reload} view={data?.view ?? null} suppliers={data?.suppliers ?? []} schedules={data?.schedules ?? []} />}
      {tab === 'retention' && <Retention policies={retention} failedDays={str(settings.retentionFailedDays, '180')} onSaved={reload} />}
      {tab === 'mail' && <Messagerie />}
    </DataGate>
  );
}

// ── Onglet Messagerie (SA-10.5) — le transport des e-mails sortants, réglé ICI plutôt que dans le `.env`.
// ⚠️ L'onglet affiche CE QUI S'APPLIQUE, pas ce qui est enregistré : un réglage incomplet retombe sur la
// configuration serveur, et le bandeau le dit. Sans ça, on croirait piloter ce qu'on ne pilote pas.
// ⚠️ Le secret ne redescend JAMAIS du serveur — le champ reste vide, et « laisser vide = inchangé ».
function Messagerie() {
  /** Le préfixe des identifiants qui relient chaque libellé à son champ. */
  const fieldId = useId();
  const { guard, showToast } = useApp();
  const [data, setData] = useState<ApiMailSettings | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [f, setF] = useState<Partial<ApiMailSettings> & { secret?: string }>({});
  const [busy, setBusy] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [testRes, setTestRes] = useState<{ ok: boolean; error?: string } | null>(null);

  const load = useCallback(() => api.get<ApiMailSettings>('/admin/settings/mail')
    .then((r) => { setData(r); setF({ ...r, secret: '' }); setErr(null); })
    .catch((e) => setErr((e as Error).message)), []);
  useEffect(() => { void load(); }, [load]);

  const save = async () => {
    if (!guard()) return;
    setBusy(true); setTestRes(null);
    try {
      const r = await api.put<ApiMailSettings>('/admin/settings/mail', {
        transport: f.transport ?? 'none', fromEmail: f.fromEmail ?? null, fromName: f.fromName ?? null,
        smtpHost: f.smtpHost ?? null, smtpPort: f.smtpPort ?? null, smtpSecure: f.smtpSecure !== false,
        smtpUser: f.smtpUser ?? null, apiKey: f.apiKey ?? null,
        ...(f.secret ? { secret: f.secret } : {}),
      });
      setData(r); setF({ ...r, secret: '' });
      showToast('Messagerie enregistrée', 'ok');
    } catch (e) { showToast('Refusé : ' + (e as Error).message, 'err'); } finally { setBusy(false); }
  };

  const test = async () => {
    if (!guard()) return;
    setBusy(true); setTestRes(null);
    try { setTestRes(await api.post<{ ok: boolean; error?: string }>('/admin/settings/mail/test', { to: testTo.trim() })); }
    catch (e) { setTestRes({ ok: false, error: (e as Error).message }); } finally { setBusy(false); }
  };

  if (err) return <Card style={{ padding: 20 }}><div style={{ color: 'var(--rose-fg)' }}>Réglages de messagerie indisponibles : {err}</div></Card>;
  if (!data) return <Card style={{ padding: 20, color: 'var(--ink-400)' }}>Chargement de la messagerie…</Card>;

  // Ce qui S'APPLIQUE, avec sa source — jamais ce qui est simplement enregistré.
  const SOURCE = {
    base: { bg: 'var(--ok-bg)', fg: 'var(--ok-fg)', txt: 'réglage de cet écran' },
    env: { bg: 'var(--amber-bg)', fg: 'var(--amber-fg)', txt: 'configuration serveur (.env)' },
    aucun: { bg: 'var(--rose-bg)', fg: 'var(--rose-fg)', txt: 'aucun envoi — les e-mails sont seulement journalisés' },
  }[data.effective];

  return (
    <Card style={{ padding: 20, maxWidth: 720 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ display: 'flex', gap: 9, padding: '10px 12px', borderRadius: 'var(--r-sm)', background: SOURCE.bg, color: SOURCE.fg, fontSize: 'var(--t-dense)', lineHeight: 1.5 }}>
          <span style={{ fontWeight: 600, flexShrink: 0 }}>ℹ</span>
          <span><b style={{ fontWeight: 600 }}>En vigueur :</b> {SOURCE.txt}. D'où partent les e-mails de la console EDI (réinitialisation de mot de passe du compte de secours). Réglable ici pour changer de fournisseur sans redéploiement — la configuration serveur reste le <b style={{ fontWeight: 600 }}>filet</b> si ce réglage est incomplet.{data.note ? ` ⚠️ ${data.note}` : ''}</span>
        </div>

        <div>
          <span id={`${fieldId}-transport`} style={lab}>Transport</span>
          <div role="group" aria-labelledby={`${fieldId}-transport`} className="seg-track" style={{ display: 'inline-flex' }}>
            {([['smtp', 'SMTP'], ['mailjet', 'Mailjet'], ['none', 'Aucun']] as const).map(([k, l]) => (
              <button key={k} type="button" className={f.transport === k ? 'seg active' : 'seg'} onClick={() => setF({ ...f, transport: k })}>{l}</button>
            ))}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div><label htmlFor={`${fieldId}-0`} style={lab}>Adresse d'expéditeur</label><input id={`${fieldId}-0`} className="field-input" value={f.fromEmail ?? ''} onChange={(e) => setF({ ...f, fromEmail: e.target.value })} placeholder="no-reply@orgazone.fr" /></div>
          <div><label htmlFor={`${fieldId}-1`} style={lab}>Nom affiché</label><input id={`${fieldId}-1`} className="field-input" value={f.fromName ?? ''} onChange={(e) => setF({ ...f, fromName: e.target.value })} placeholder="Orgazone EDI" /></div>
        </div>

        {f.transport === 'smtp' && (
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 12 }}>
            <div><label htmlFor={`${fieldId}-2`} style={lab}>Serveur SMTP</label><input id={`${fieldId}-2`} style={{ ...inputSt, fontFamily: 'var(--font-mono)' }} value={f.smtpHost ?? ''} onChange={(e) => setF({ ...f, smtpHost: e.target.value })} placeholder="ssl0.ovh.net" /></div>
            <div><label htmlFor={`${fieldId}-3`} style={lab}>Port</label><input id={`${fieldId}-3`} style={{ ...inputSt, fontFamily: 'var(--font-mono)' }} inputMode="numeric" value={f.smtpPort ?? ''} onChange={(e) => setF({ ...f, smtpPort: Number(e.target.value.replace(/\D/g, '')) || null })} placeholder="465" /></div>
            <div>
              <span id={`${fieldId}-secure`} style={lab}>Chiffrement</span>
              <div role="group" aria-labelledby={`${fieldId}-secure`} className="seg-track">
                {([[true, 'SSL/TLS'], [false, 'STARTTLS']] as const).map(([k, l]) => (
                  <button key={String(k)} type="button" className={(f.smtpSecure !== false) === k ? 'seg active' : 'seg'} onClick={() => setF({ ...f, smtpSecure: k })}>{l}</button>
                ))}
              </div>
            </div>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {f.transport === 'smtp' && <div><label htmlFor={`${fieldId}-4`} style={lab}>Identifiant</label><input id={`${fieldId}-4`} style={{ ...inputSt, fontFamily: 'var(--font-mono)' }} value={f.smtpUser ?? ''} onChange={(e) => setF({ ...f, smtpUser: e.target.value })} /></div>}
          {f.transport === 'mailjet' && <div><label htmlFor={`${fieldId}-5`} style={lab}>Clé API</label><input id={`${fieldId}-5`} style={{ ...inputSt, fontFamily: 'var(--font-mono)' }} value={f.apiKey ?? ''} onChange={(e) => setF({ ...f, apiKey: e.target.value })} /></div>}
          {f.transport !== 'none' && (
            <div>
              <label htmlFor={`${fieldId}-6`} style={lab}>{f.transport === 'mailjet' ? 'Secret API' : 'Mot de passe'}</label>
              <input id={`${fieldId}-6`} style={{ ...inputSt, fontFamily: 'var(--font-mono)' }} type="password" autoComplete="new-password" value={f.secret ?? ''}
                onChange={(e) => setF({ ...f, secret: e.target.value })}
                placeholder={data.secretSetAt ? '•••••••• (inchangé)' : 'à renseigner'} />
              {/* Le secret ne redescend jamais : on ne peut donc que le REMPLACER, jamais le relire. */}
              <div style={{ fontSize: 'var(--t-sub)', color: 'var(--ink-400)', marginTop: 3 }}>
                {data.secretSetAt ? `Défini le ${dfDateOnly(data.secretSetAt)} — laisser vide pour le conserver.` : 'Jamais défini.'}
              </div>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', borderTop: '1px solid var(--ink-200)', paddingTop: 14 }}>
          <Btn disabled={busy} onClick={() => void save()}>{busy ? 'Enregistrement…' : 'Enregistrer'}</Btn>
          {/* ⚠️ L'ESSENTIEL de cet onglet : on SAIT que ça marche au lieu de le supposer. L'erreur rendue
              est celle du transport (« auth failed », « ENOTFOUND ») — c'est elle qui dit quoi corriger. */}
          <input className="field-input" style={{ width: 230 }} value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="Envoyer un test à…" />
          <Btn disabled={busy || !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(testTo.trim())} onClick={() => void test()}>Envoyer un test</Btn>
          {testRes && (
            <span style={{ fontSize: 'var(--t-dense)', color: testRes.ok ? 'var(--ok-fg)' : 'var(--rose-fg)', lineHeight: 1.45 }}>
              {testRes.ok ? '✓ Message parti.' : `✕ ${testRes.error ?? 'échec'}`}
            </span>
          )}
        </div>
      </div>
    </Card>
  );
}

// Onglet Général — canaux d'alerte + dispatch nocturne + fuseau, persistés en `edi_setting`.
function General({ settings, onSaved }: { settings: Record<string, unknown>; onSaved: () => void }) {
  /** Le préfixe des identifiants qui relient chaque libellé à son champ. */
  const fieldId = useId();
  const { guard, showToast } = useApp();
  const [mail, setMail] = useState(str(settings.alertEmails));
  const [hook, setHook] = useState(str(settings.alertWebhook));
  const [tz, setTz] = useState(str(settings.timezone, 'Europe/Paris'));
  const [dispatch, setDispatch] = useState(str(settings.dispatchTime, '23:30'));
  const [retDefault, setRetDefault] = useState(str(settings.retentionDefaultDays, '90'));
  const [retFailed, setRetFailed] = useState(str(settings.retentionFailedDays, '180'));
  const [busy, setBusy] = useState(false);
  const noChannel = !mail.trim() && !hook.trim();

  const save = async () => {
    if (!guard() || noChannel) return;
    setBusy(true);
    try {
      const r = await api.put<{ saved: number; applied: boolean }>('/admin/settings', { alertEmails: mail.trim(), alertWebhook: hook.trim(), timezone: tz, dispatchTime: dispatch, retentionDefaultDays: retDefault, retentionFailedDays: retFailed });
      // `applied` = le dispatch nocturne a été re-planifié À CHAUD ; sinon il le sera au prochain redémarrage.
      showToast(r.applied ? 'Réglages enregistrés' : 'Réglages enregistrés — appliqués au prochain redémarrage', r.applied ? 'ok' : 'warn');
      onSaved();
    } catch (e) { showToast('Échec : ' + (e as Error).message, 'err'); } finally { setBusy(false); }
  };

  return (
    <Card style={{ padding: 20, maxWidth: 640 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Honnêteté §23.0 : dire ce qui est BRANCHÉ et ce qui ne l'est pas encore — jamais le simuler. */}
        <div style={{ display: 'flex', gap: 9, padding: '10px 12px', borderRadius: 'var(--r-sm)', background: 'var(--blue-bg)', color: 'var(--blue-fg)', fontSize: 'var(--t-dense)', lineHeight: 1.5 }}>
          <span style={{ fontWeight: 600, flexShrink: 0 }}>ℹ</span>
          <span><b style={{ fontWeight: 600 }}>Branché :</b> l'heure du dispatch nocturne pilote réellement le dépôt sortant ; la <b style={{ fontWeight: 600 }}>rétention</b> (par défaut / échecs) pilote réellement la purge (brut + ligne retirés à expiration) ; le <b style={{ fontWeight: 600 }}>fuseau</b> fixe l'heure de toutes les planifications (relèves, dépôt, purge). Un message passe en <b style={{ fontWeight: 600 }}>dead-letter</b> quand la file a épuisé ses six tentatives automatiques — ce n'est pas un réglage. <b style={{ fontWeight: 600 }}>Pas encore branché :</b> l'envoi d'alertes (e-mail/webhook) — les destinataires sont enregistrés, mais le module d'alertes n'existe pas encore : aucun envoi n'a lieu.</span>
        </div>
        <div>
          <label htmlFor={`${fieldId}-0`} style={lab}>Alertes par e-mail</label>
          <input id={`${fieldId}-0`} className="field-input" value={mail} onChange={(e) => setMail(e.target.value)} placeholder="destinataires, séparés par des virgules" />
        </div>
        <div>
          <label htmlFor={`${fieldId}-1`} style={lab}>Alertes par webhook</label>
          <input id={`${fieldId}-1`} style={{ ...inputSt, fontFamily: 'var(--font-mono)' }} value={hook} onChange={(e) => setHook(e.target.value)} placeholder="https://…" />
        </div>
        {noChannel && <div style={{ background: 'var(--rose-bg)', color: 'var(--rose-fg)', borderRadius: 'var(--r-sm)', padding: '9px 11px', fontSize: 'var(--t-dense)' }}>Au moins un canal d'alerte est requis (dead-letter, transport en échec, retard de dispatch).</div>}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div><label htmlFor={`${fieldId}-3`} style={lab}>Dispatch nocturne</label><input id={`${fieldId}-3`} className="field-input" type="time" value={dispatch} onChange={(e) => setDispatch(e.target.value)} /></div>
          <div><label htmlFor={`${fieldId}-4`} style={lab}>Fuseau de référence</label><select id={`${fieldId}-4`} className="field-input" value={tz} onChange={(e) => setTz(e.target.value)}><option>Europe/Paris</option><option>Europe/London</option><option>UTC</option></select></div>
        </div>
        {/* Rétention CONFIGURABLE (§10) — BRANCHÉE : le job de purge retire brut + ligne après ces délais. */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div><label htmlFor={`${fieldId}-5`} style={lab}>Rétention par défaut</label><input id={`${fieldId}-5`} className="field-input" value={retDefault} onChange={(e) => setRetDefault(e.target.value.replace(/\D/g, ''))} /><div style={{ fontSize: 'var(--t-sub)', color: 'var(--ink-400)', marginTop: 3 }}>jours — messages aboutis (confirmés) sans politique par type</div></div>
          <div><label htmlFor={`${fieldId}-6`} style={lab}>Rétention des échecs</label><input id={`${fieldId}-6`} className="field-input" value={retFailed} onChange={(e) => setRetFailed(e.target.value.replace(/\D/g, ''))} /><div style={{ fontSize: 'var(--t-sub)', color: 'var(--ink-400)', marginTop: 3 }}>jours — non reconnus / en erreur (temps d'investiguer/rejouer)</div></div>
        </div>
        <div><Btn disabled={busy || noChannel} onClick={() => void save()}>{busy ? 'Enregistrement…' : 'Enregistrer'}</Btn></div>
      </div>
    </Card>
  );
}

// ONGLET PLANIFICATIONS — LE SEUL ENDROIT où l'on règle QUI est relevé et QUAND.
//
// ⚠️ 🔴 UN SEUL ENDROIT, ET C'EST UNE DÉCISION UTILISATEUR. Une première version offrait le rythme d'un
// fournisseur dans SA fiche et un rythme commun ici : deux écrans pour le même réglage, donc deux endroits
// où chercher et deux endroits à tenir d'accord. « On planifie tout depuis ici et pour tout le monde. »
//
// ⚠️ 🔴 IL N'Y A PLUS DE RYTHME COMMUN : « chaque fournisseur a son rythme, donc le rythme global ne me sert
// à rien ». Un défaut partagé relevait un fournisseur à une heure que personne n'avait choisie POUR LUI.
// Conséquence assumée : **un fournisseur sans rythme n'est pas relevé**, et l'écran doit le dire fort.
//
// ⚠️ UNE SEULE LISTE POUR TOUT CE QUI EST PLANIFIÉ, relèves ET dépôt nocturne — comme la maquette. « Quand
// va-t-on chercher » et « quand va-t-on déposer » sont la même question pour l'exploitant ; séparés, il faut
// se souvenir de ce que dit l'autre écran.
//
// ⚠️ CHAQUE LIGNE ANNONCE SON PROCHAIN PASSAGE, calculé côté serveur par l'ÉVALUATEUR DE pg-boss — pas par
// nous. C'est ce qui rend l'heure vraie plutôt que plausible (§23.0) ; `null` s'affiche « heure inconnue ».
function Planifications({ onSaved, view, suppliers, schedules }: {
  onSaved: () => void;
  view: ApiSchedulesView | null; suppliers: ApiSupplierRow[]; schedules: ApiSchedule[];
}) {
  const parRythme = new Map((view?.fetch ?? []).map((x) => [x.supplierId, x]));
  const actifs = suppliers.filter((x) => x.active);
  const lignes = actifs.map((s) => {
    const own = schedules.find((x) => x.supplierId === s.id) ?? null;
    return {
      sup: s,
      own,
      rythme: parRythme.get(s.id) ?? null,
      // ⚠️ LE VERDICT VIENT DE LA RÈGLE PARTAGÉE, pas d'un test écrit ici : le serveur tranche, l'écran
      //    explique. Deux façons d'y répondre finiraient par diverger (`fetch-state.ts`).
      etat: fetchStateOf(parRythme.get(s.id), {
        active: s.active,
        accounts: s.transportCount,
        activeAccounts: s.activeTransportCount,
        hasRhythm: !!own,
        rhythmActive: own?.active ?? false,
      }),
    };
  });

  // ⚠️ LA PREMIÈRE LIGNE NON RELEVÉE EST LE MEILLEUR POINT D'ENTRÉE : c'est le seul état qui demande une
  //    action. À défaut, le premier fournisseur — jamais rien, ce qui laisserait l'éditeur sans sujet.
  const [sel, setSel] = useState<string>(() => (lignes.find((l) => !l.etat.scheduled) ?? lignes[0])?.sup.id ?? '');
  const choisi = lignes.find((l) => l.sup.id === sel) ?? null;
  const nonReleves = lignes.filter((l) => !l.etat.scheduled).length;

  // ⚠️ CHAQUE RANGÉE PORTE SON ÉTAT : l'heure du prochain passage en `hint`, le verdict en `badge`. C'est ce
  //    qui évite que le sélecteur ne coûte la vue d'ensemble — ouvrir la liste EST la vue d'ensemble.
  const options: PickerOption[] = lignes.map((l) => ({
    value: l.sup.id,
    label: l.sup.name,
    hint: l.rythme ? prochainCourt(l.rythme.nextAt) : '—',
    badge: l.etat.scheduled
      ? { label: 'Relevé', tone: 'ok' as const }
      : { label: 'Pas relevé', tone: 'warn' as const },
  }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 760 }}>
      <Card style={{ padding: 20 }}>
        <div style={{ fontSize: 'var(--t-title)', fontWeight: 700, color: 'var(--ink-900)' }}>Quand va-t-on chercher les fichiers ?</div>
        <div style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-500)', marginTop: 3, marginBottom: 14 }}>
          Chaque fournisseur a <b>son</b> rythme. Sans rythme, il n’est pas relevé — aucune heure par défaut ne s’applique à sa place.
          {view && <> Heures en <b>{view.timezone}</b>.</>}
        </div>

        {/* ⚠️ 🔴 UN SÉLECTEUR POUR CHOISIR, DES LIGNES POUR LIRE — et c'est la résolution d'une contradiction
            entre deux exigences. « Une liste à plat sera ingérable à 50 fournisseurs » (décision utilisateur)
            interdit de dérouler N rangées en permanence ; la maquette, elle, montre une LISTE. Le picker
            porte donc la sélection ET la vue d'ensemble — chacune de ses rangées annonce déjà l'heure du
            prochain passage et son verdict —, tandis que la forme de ligne de la maquette rend ce qui est
            BORNÉ : la relève choisie, et le dépôt nocturne. */}
        {lignes.length === 0
          ? <div style={{ padding: 14, fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>Aucun fournisseur actif.</div>
          : (
            <Picker
              id="edi-planif-cible"
              variant="field"
              label="Fournisseur"
              allLabel="Choisir un fournisseur"
              options={options}
              value={sel}
              onChange={setSel}
            />
          )}

        <div style={{ border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', overflow: 'hidden', margin: '14px 0' }}>
          {choisi && (
            <LignePlanifiee
              on
              kind={choisi.etat.scheduled ? 'ok' : choisi.etat.why === 'rhythm-paused' ? 'off' : 'warn'}
              nom={`Relève ${choisi.sup.name}`}
              portee={choisi.sup.transport ? `${choisi.sup.transport.type} · ${choisi.sup.activeTransportCount} compte(s) actif(s)` : 'aucun compte de transport'}
              expr={choisi.own ? (choisi.rythme?.cron ?? 'ne déclenche jamais') : 'aucun rythme'}
              prochain={choisi.rythme?.nextAt ?? null}
              action={choisi.own ? { label: choisi.own.active ? 'Mettre de côté' : 'Réactiver', supplierId: choisi.sup.id, active: !choisi.own.active, onDone: onSaved } : null}
            />
          )}

          {/* ⚠️ LE DÉPÔT NOCTURNE EST DANS LA MÊME LISTE, mais son heure se règle dans Général : l'écran le
              DIT au lieu d'offrir un second champ, qui serait une seconde voie d'écriture sur le même fait. */}
          {view && (
            <LignePlanifiee
              on={false}
              kind={view.dispatch.pending > 0 ? 'warn' : 'ok'}
              nom="Dépôt sortant nocturne"
              portee={`tous les clients · ${view.dispatch.pending} en file`}
              expr={view.dispatch.cron ?? 'heure illisible'}
              prochain={view.dispatch.nextAt}
              note="Son heure se règle dans l’onglet Général."
              action={null}
            />
          )}
        </div>

        {/* ⚠️ 🔴 CE COMPTE NE DOIT PAS SE PERDRE DANS LA LISTE. Ceux que personne ne relève sont le seul état
            qui demande une action, et il est devenu FRÉQUENT depuis qu'il n'y a plus d'heure par défaut :
            tout fournisseur créé naît ainsi (§23.0). */}
        {nonReleves > 0 && (
          <div style={{ padding: 12, borderRadius: 'var(--r-sm)', background: 'var(--amber-bg)', color: 'var(--amber-fg)', fontSize: 'var(--t-body)' }}>
            {nonReleves === 1 ? '1 fournisseur actif n’est pas relevé' : `${nonReleves} fournisseurs actifs ne sont pas relevés`} — repérables à leur pastille « Pas relevé ».
          </div>
        )}

        {/* ⚠️ LA CAUSE SE LIT SOUS LA LISTE, pas dans la rangée : elle est trop longue pour une rangée, et
            l'y tasser la rendrait illisible là où elle doit justement être comprise. */}
        {choisi && !choisi.etat.scheduled && (
          <div style={{ marginTop: 10, padding: 12, borderRadius: 'var(--r-sm)', background: 'var(--rose-bg)', color: 'var(--rose-fg)', fontSize: 'var(--t-body)' }}>
            {WHY_TEXT[choisi.etat.why]}
          </div>
        )}
      </Card>

      {/* ⚠️ `key` : changer de fournisseur doit REPARTIR de SON rythme. Sans remontage, l'assistant garderait
          la saisie du précédent — et on enregistrerait le rythme d'un fournisseur sur un autre. */}
      {choisi && (
        <EditeurDeRythme
          key={choisi.sup.id}
          titre={choisi.sup.name}
          sousTitre={choisi.own
            ? 'Modifier son heure de relève, ou la retirer — il cessera alors d’être relevé.'
            : 'Ce fournisseur n’a pas encore d’heure de relève. Donnez-lui la sienne.'}
          depart={parseSched(choisi.own?.expression)}
          supplierId={choisi.sup.id}
          aDejaLeSien={!!choisi.own}
          timezone={view?.timezone ?? null}
          onSaved={onSaved}
        />
      )}
    </div>
  );
}

/**
 * L'HEURE DU PROCHAIN PASSAGE, en clair.
 *
 * ⚠️ 🔴 `null` SE DIT « heure inconnue », jamais « bientôt » ni une date devinée : le serveur rend `null`
 * quand l'évaluateur refuse l'expression, et inventer une heure serait le pire mensonge de cet écran —
 * l'exploitant attendrait une relève qui n'arrive pas (§23.0).
 * ⚠️ PAS DE SECONDES : pg-boss déclenche à la minute (il tire quand l'occurrence précédente a moins de 60 s),
 * donc afficher une seconde serait une précision inventée.
 */
function prochainCourt(iso: string | null): string {
  if (!iso) return 'heure inconnue';
  return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

/** Le délai jusqu'au prochain passage — « dans 1 h 56 », « dans 3 min ». Rien si l'heure est inconnue. */
function delaiCourt(iso: string | null): string {
  if (!iso) return '';
  const min = Math.round((new Date(iso).getTime() - Date.now()) / 60000);
  if (min < 0) return 'imminent';
  if (min < 60) return `dans ${min} min`;
  const h = Math.floor(min / 60);
  const r = min % 60;
  return r === 0 ? `dans ${h} h` : `dans ${h} h ${r}`;
}

/**
 * UNE LIGNE DE LA LISTE DES PLANIFICATIONS.
 *
 * ⚠️ LA MÊME FORME POUR UNE RELÈVE ET POUR LE DÉPÔT : c'est ce qui fait de la liste UNE liste. Deux rendus
 * distincts pour deux planifications se seraient mis à divergir sur ce qu'ils montrent.
 * ⚠️ LE NOM EST DÉRIVÉ, JAMAIS SAISI (écart assumé avec la maquette, qui offre un champ libre) : un nom tapé
 * à la main peut mentir sur sa portée — « Relève Bestseller » pointant Street One se lit sans se voir.
 */
function LignePlanifiee({ on, onPick, kind, nom, portee, expr, prochain, note, action }: {
  on: boolean; onPick?: () => void; kind: DotKind; nom: string; portee: string;
  expr: string; prochain: string | null; note?: string;
  action: { label: string; supplierId: string; active: boolean; onDone: () => void } | null;
}) {
  const { guard, showToast } = useApp();
  const [busy, setBusy] = useState(false);

  const basculer = async () => {
    if (!action || !guard()) return;
    setBusy(true);
    try {
      const r = await api.patch<{ applied: boolean }>(`/admin/schedules/supplier/${action.supplierId}`, { active: action.active });
      showToast(
        action.active
          ? (r.applied ? 'Rythme réactivé' : 'Rythme réactivé — appliqué au prochain redémarrage')
          : (r.applied ? 'Rythme mis de côté — son heure est conservée' : 'Rythme mis de côté — appliqué au prochain redémarrage'),
        r.applied ? 'ok' : 'warn',
      );
      action.onDone();
    } catch (e) { showToast('Échec : ' + (e as Error).message, 'err'); } finally { setBusy(false); }
  };

  return (
    <div
      onClick={onPick}
      style={{
        display: 'flex', alignItems: 'center', gap: 13, padding: '11px 14px', flexWrap: 'wrap',
        borderBottom: '1px solid var(--ink-100)', cursor: onPick ? 'pointer' : 'default',
        background: on ? 'var(--ink-50)' : 'var(--paper)',
        boxShadow: on ? 'inset 2px 0 0 var(--teal-500)' : 'none',
      }}
    >
      <Dot kind={kind} />
      <div style={{ flex: '1 1 180px', minWidth: 0 }}>
        {/* ⚠️ LE NOM EST LE BOUTON QUI CHOISIT LA LIGNE, COMME ELLE : un `div` cliquable n'existe pas au clavier, et le
            bouton de la ligne BASCULE le rythme, il ne le choisit pas. Il porte son geste — vivre par la remontée
            jusqu'à la ligne serait un bouton mort en sursis. */}
        {onPick ? (
          <button
            type="button" aria-pressed={on} onClick={(e) => { e.stopPropagation(); onPick(); }}
            style={{ display: 'block', padding: 0, border: 'none', background: 'transparent', font: 'inherit', textAlign: 'left', cursor: 'pointer', fontSize: 'var(--t-body)', fontWeight: 600, color: 'var(--ink-800)' }}
          >
            {nom}
          </button>
        ) : <div style={{ fontSize: 'var(--t-body)', fontWeight: 600, color: 'var(--ink-800)' }}>{nom}</div>}
        <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)' }}>{portee}</div>
      </div>
      <div style={{ flex: '1 1 140px', minWidth: 0, fontFamily: 'var(--font-mono)', fontSize: 'var(--t-note)', color: 'var(--ink-700)' }}>{expr}</div>
      <div style={{ textAlign: 'right', flex: '0 0 auto' }}>
        <div style={{ fontSize: 'var(--t-note)', fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>{prochainCourt(prochain)}</div>
        <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-400)' }}>{note ?? delaiCourt(prochain)}</div>
      </div>
      {action && (
        <Btn size="sm" variant="ghost" disabled={busy} onClick={() => void basculer()}>{action.label}</Btn>
      )}
    </div>
  );
}

/**
 * L'ÉDITEUR DU RYTHME D'UN FOURNISSEUR.
 *
 * ⚠️ 🔴 « RETIRER » N'EST PLUS UN REPLI, C'EST UN ARRÊT. Tant qu'un rythme commun existait, retirer le
 * rythme propre faisait suivre le commun ; il n'existe plus, donc retirer ARRÊTE la relève. Le libellé le
 * dit — promettre un repli disparu serait exactement la phrase qu'un correctif laisse derrière lui (§23.0).
 */
function EditeurDeRythme({ titre, sousTitre, depart, supplierId, aDejaLeSien, timezone, onSaved }: {
  titre: string; sousTitre: string; depart: SchedVals;
  supplierId: string; aDejaLeSien: boolean; timezone: string | null; onSaved: () => void;
}) {
  const { guard, showToast } = useApp();
  const [v, setV] = useState<SchedVals>(depart);
  const [busy, setBusy] = useState(false);
  const r = schedCalc(v);

  const enregistrer = async () => {
    if (!guard() || !r.ready) return;
    setBusy(true);
    try {
      // On stocke la config COMPLÈTE (round-trip de l'assistant) ; le serveur la traduit en cron et
      // réconcilie pg-boss à chaud — l'aperçu « Ce qui se passera » est donc la réalité (§23.0).
      const res = await api.put<{ cron: string; applied: boolean }>(`/admin/schedules/supplier/${supplierId}`, { expression: JSON.stringify(v) });
      showToast(res.applied ? 'Rythme enregistré et appliqué' : 'Rythme enregistré — appliqué au prochain redémarrage', res.applied ? 'ok' : 'warn');
      onSaved();
    } catch (e) { showToast('Échec : ' + (e as Error).message, 'err'); } finally { setBusy(false); }
  };

  const retirer = async () => {
    if (!guard()) return;
    setBusy(true);
    try {
      const res = await api.del<{ applied: boolean }>(`/admin/schedules/supplier/${supplierId}`);
      showToast(res.applied ? 'Rythme retiré — ce fournisseur n’est plus relevé' : 'Rythme retiré — pris en compte au prochain redémarrage', res.applied ? 'ok' : 'warn');
      onSaved();
    } catch (e) { showToast('Échec : ' + (e as Error).message, 'err'); } finally { setBusy(false); }
  };

  return (
    <Card style={{ padding: 20 }}>
      <div style={{ fontSize: 'var(--t-title)', fontWeight: 700, color: 'var(--ink-900)' }}>{titre}</div>
      <div style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-500)', marginTop: 3, marginBottom: 16 }}>{sousTitre}</div>

      <RhythmAssistant value={v} onChange={setV} />

      <div style={{ marginTop: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Btn disabled={busy || !r.ready} onClick={() => void enregistrer()}>
          {busy ? 'Enregistrement…' : aDejaLeSien ? 'Mettre à jour son rythme' : 'Lui donner ce rythme'}
        </Btn>
        {aDejaLeSien && (
          <Btn variant="ghost" disabled={busy} onClick={() => void retirer()}>Retirer — cesser de le relever</Btn>
        )}
      </div>

      <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink-500)', marginTop: 12 }}>
        Heures exprimées en <b>{timezone ?? 'fuseau inconnu'}</b> — réglable dans l’onglet <b>Général</b>.
      </div>
    </Card>
  );
}

// Onglet Rétention — une durée par type (+ défaut « * ») persistée en `retention_policy` ; la purge l'applique.
function Retention({ policies, failedDays, onSaved }: { policies: ApiRetention[]; failedDays: string; onSaved: () => void }) {
  const { guard, showToast } = useApp();
  const byType = Object.fromEntries(policies.map((p) => [p.messageType, p.durationDays]));
  const fallback = byType['*'] ?? 90;
  // Lignes : le défaut (*) + un type par MSG_TYPES, pré-rempli depuis la base (ou le défaut).
  const ROWS = [{ key: '*', label: 'Défaut (tout autre type)' }, ...MSG_TYPES.map((t) => ({ key: t.key, label: t.label }))];
  const [days, setDays] = useState<Record<string, number>>(() => Object.fromEntries(ROWS.map((r) => [r.key, byType[r.key] ?? (r.key === '*' ? fallback : fallback)])));
  const [busy, setBusy] = useState(false);
  const reduced = ROWS.some((r) => (days[r.key] ?? 0) < (byType[r.key] ?? Infinity));

  const save = async () => {
    if (!guard()) return;
    setBusy(true);
    try {
      await api.put('/admin/retention', { policies: ROWS.map((r) => ({ messageType: r.key, durationDays: days[r.key] ?? fallback })) });
      showToast('Rétention enregistrée', 'ok');
      onSaved();
    } catch (e) { showToast('Échec : ' + (e as Error).message, 'err'); } finally { setBusy(false); }
  };

  return (
    <Card style={{ padding: 20, maxWidth: 720 }}>
      <div style={{ fontSize: 'var(--t-title)', fontWeight: 700, color: 'var(--ink-900)' }}>Combien de temps garde-t-on les fichiers ?</div>
      {/* ⚠️ « LE REJEU RESTE POSSIBLE D'ICI LÀ » est la phrase de la maquette, et elle manquait : la durée de
          rétention n'est pas qu'une règle de ménage — c'est la FENÊTRE pendant laquelle un message en erreur
          peut encore être rejoué. La réduire, c'est raccourcir le délai de réparation, pas seulement libérer
          du disque. L'opérateur qui tape un chiffre doit savoir laquelle des deux choses il décide. */}
      <div style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-500)', marginTop: 3, marginBottom: 16 }}>Une durée par type de message. Le brut + le canonique sont conservés jusqu'à expiration — <strong>le rejeu reste possible d'ici là</strong> —, puis tout est purgé.</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {ROWS.map((r) => (
          <div key={r.key} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', border: '1px solid var(--ink-100)', borderRadius: 'var(--r-sm)' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 'var(--t-body)', fontWeight: 600, color: 'var(--ink-800)', fontFamily: 'var(--font-mono)' }}>{r.key}</div>
              <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)' }}>{r.label}</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input aria-label={`${r.label} — jours`} value={days[r.key] ?? fallback} onChange={(e) => setDays((d) => ({ ...d, [r.key]: Number(e.target.value.replace(/\D/g, '')) || 0 }))} style={{ width: 64, height: 34, textAlign: 'right', padding: '0 10px', borderRadius: 'var(--r-xs)', border: '1px solid var(--ink-200)', background: 'var(--paper)', color: 'var(--ink-900)', fontSize: 'var(--t-lead)', fontVariantNumeric: 'tabular-nums' }} />
              <span style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-500)' }}>jours</span>
            </div>
          </div>
        ))}
      </div>
      {/* ⚠️ 🔴 LES ÉCHECS ONT LEUR DURÉE, ET ELLE MANQUAIT À CE TABLEAU (relevé de la maquette, qui lui donne
          une ligne « — non reconnu / brut conservé »). Elle EXISTE déjà, en réglage, dans l'onglet Général :
          un message non reconnu ou en erreur est stampé avec `retentionFailedDays`, pas avec la durée de son
          type. La taire ici laissait croire qu'un entrant non reconnu suivait la ligne « Défaut ».
          ⚠️ AFFICHÉE, PAS ÉDITABLE : en faire un second champ donnerait DEUX commandes du même fait, qui
          divergeraient au premier enregistrement (Forme A). On montre la valeur et on dit où elle se règle. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', border: '1px dashed var(--ink-200)', borderRadius: 'var(--r-sm)', marginTop: 8, background: 'var(--ink-50)' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 'var(--t-body)', fontWeight: 600, color: 'var(--ink-700)', fontFamily: 'var(--font-mono)' }}>— non reconnu / en erreur</div>
          <div style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)' }}>brut conservé · se règle dans l’onglet <strong>Général</strong></div>
        </div>
        <div style={{ fontSize: 'var(--t-lead)', color: 'var(--ink-600)', fontVariantNumeric: 'tabular-nums' }}>{failedDays} jours</div>
      </div>
      {reduced && <div style={{ background: 'var(--amber-bg)', color: 'var(--amber-fg)', borderRadius: 'var(--r-sm)', padding: '9px 11px', fontSize: 'var(--t-dense)', marginTop: 14 }}>⚠️ Les échéances de TOUS les messages (y compris déjà reçus) sont recalculées à l'enregistrement : réduire une durée fera <strong>purger les messages confirmés</strong> hors de la nouvelle fenêtre au prochain passage de purge (03:15).</div>}
      <div style={{ marginTop: 16 }}><Btn disabled={busy} onClick={() => void save()}>{busy ? 'Enregistrement…' : 'Enregistrer'}</Btn></div>
    </Card>
  );
}
